import { TeamsPhotoPost, TeamsPhotoComment } from '../types';
import { Database, ref, push, set, get, query, orderByChild, equalTo, remove, update, DataSnapshot } from 'firebase/database';

export class TeamsPhotoFirebaseService {
    private static instance: TeamsPhotoFirebaseService;
    private database: Database | null = null;
    private databasePath: string = 'teamsPhotos'; // Default path, can be overridden

    private constructor() {
        // Don't initialize Firebase here - let the calling app provide the database instance
    }

    /**
     * Set the Firebase database instance
     */
    public setDatabase(database: Database): void {
        this.database = database;
    }

    /**
     * Set the database path for teamsPhotos
     */
    public setDatabasePath(path: string): void {
        this.databasePath = path;
    }

    /**
     * Get singleton instance for backward compatibility
     */
    public static getInstance(): TeamsPhotoFirebaseService {
        if (!TeamsPhotoFirebaseService.instance) {
            TeamsPhotoFirebaseService.instance = new TeamsPhotoFirebaseService();
        }
        return TeamsPhotoFirebaseService.instance;
    }

    /**
     * Save photo post metadata to Firebase
     */
    async savePhotoPost(photoPost: TeamsPhotoPost): Promise<string> {
        if (!this.database) {
            throw new Error('Firebase database not initialized. Call setDatabase() first.');
        }

        try {
            const photoRef = ref(this.database, this.databasePath);
            const newPhotoRef = push(photoRef);

            // Clean the photo post data to remove undefined values and ensure all fields are valid
            // Note: Don't include 'id' field when saving to Firebase - it will be generated by push()
            // Explicitly create object without id field to avoid any destructuring issues
            const cleanPhotoPost = {
                challengeId: photoPost.challengeId,
                userId: photoPost.userId,
                userDisplayName: photoPost.userDisplayName,
                photoUrl: photoPost.photoUrl,
                teamsMessageId: photoPost.teamsMessageId,
                teamsChannelId: photoPost.teamsChannelId,
                caption: photoPost.caption === undefined ? null : photoPost.caption, // Explicitly handle undefined
                postedAt: photoPost.postedAt,
                likes: photoPost.likes || 0,
                comments: photoPost.comments || [],
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                // Only include optional fields if they have values
                ...(photoPost.blobName && { blobName: photoPost.blobName }),
                ...(photoPost.oneDriveFileId && { oneDriveFileId: photoPost.oneDriveFileId }),
                ...(photoPost.oneDriveDriveId && { oneDriveDriveId: photoPost.oneDriveDriveId }),
                ...(photoPost.oneDriveItemId && { oneDriveItemId: photoPost.oneDriveItemId }),
                // Include encryption metadata fields (always include if they exist, even if empty)
                ...(photoPost.encryptionKey !== undefined && { encryptionKey: photoPost.encryptionKey }),
                ...(photoPost.encryptionAlgorithm !== undefined && { encryptionAlgorithm: photoPost.encryptionAlgorithm }),
                ...(photoPost.ivLength !== undefined && { ivLength: photoPost.ivLength }),
                ...(photoPost.originalContentType !== undefined && { originalContentType: photoPost.originalContentType }),
                ...(photoPost.originalSize !== undefined && { originalSize: photoPost.originalSize }),
                ...(photoPost.optimizedSize !== undefined && { optimizedSize: photoPost.optimizedSize }),
                ...(photoPost.compressionRatio !== undefined && { compressionRatio: photoPost.compressionRatio })
            };

            // Additional debugging to see what we're working with
            console.log('[TeamsPhotoFirebaseService] Original photoPost:', {
                hasId: 'id' in photoPost,
                idValue: photoPost.id,
                keys: Object.keys(photoPost),
                hasEncryptionKey: 'encryptionKey' in photoPost,
                encryptionKeyValue: photoPost.encryptionKey ? `${photoPost.encryptionKey.substring(0, 20)}...` : 'undefined/empty'
            });
            console.log('[TeamsPhotoFirebaseService] Clean photoPost:', {
                hasId: 'id' in cleanPhotoPost,
                keys: Object.keys(cleanPhotoPost),
                hasEncryptionKey: 'encryptionKey' in cleanPhotoPost,
                encryptionKeyValue: cleanPhotoPost.encryptionKey ? `${cleanPhotoPost.encryptionKey.substring(0, 20)}...` : 'undefined/empty'
            });

            // Debug logging to see exactly what we're sending to Firebase
            console.log('[TeamsPhotoFirebaseService] Saving clean photo post to Firebase:', {
                cleanPhotoPost,
                hasIdField: 'id' in cleanPhotoPost,
                idValue: (cleanPhotoPost as any).id,
                databasePath: this.databasePath,
                newPhotoRef: newPhotoRef.toString()
            });

            await set(newPhotoRef, cleanPhotoPost);

            return newPhotoRef.key || photoPost.id || '';
        } catch (error) {
            console.error('Error saving photo post to Firebase:', error);
            throw new Error(`Failed to save photo post: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Get all photos for a specific challenge
     */
    async getChallengePhotos(challengeId: string): Promise<TeamsPhotoPost[]> {
        if (!this.database) {
            throw new Error('Firebase database not initialized. Call setDatabase() first.');
        }

        try {
            const photosRef = ref(this.database, this.databasePath);
            const challengePhotosQuery = query(
                photosRef,
                orderByChild('challengeId'),
                equalTo(challengeId)
            );

            const snapshot = await get(challengePhotosQuery);
            const photos: TeamsPhotoPost[] = [];

            if (snapshot.exists()) {
                snapshot.forEach((childSnapshot: DataSnapshot) => {
                    const photoData = childSnapshot.val();
                    photos.push({
                        ...photoData,
                        id: childSnapshot.key || photoData.id
                    });
                });
            }

            // Sort by posted date (newest first)
            return photos.sort((a, b) => new Date(b.postedAt).getTime() - new Date(a.postedAt).getTime());
        } catch (error) {
            console.error('Error getting challenge photos from Firebase:', error);
            throw new Error(`Failed to get challenge photos: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Get a specific photo by ID
     */
    async getPhotoById(photoId: string): Promise<TeamsPhotoPost | null> {
        if (!this.database) {
            throw new Error('Firebase database not initialized. Call setDatabase() first.');
        }

        try {
            const photoRef = ref(this.database, `${this.databasePath}/${photoId}`);
            const snapshot = await get(photoRef);

            if (snapshot.exists()) {
                const photoData = snapshot.val();
                return {
                    ...photoData,
                    id: snapshot.key || photoData.id
                };
            }

            return null;
        } catch (error) {
            console.error('Error getting photo by ID from Firebase:', error);
            throw new Error(`Failed to get photo: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Update photo post (e.g., likes, comments)
     */
    async updatePhotoPost(photoId: string, updates: Partial<TeamsPhotoPost>): Promise<void> {
        if (!this.database) {
            throw new Error('Firebase database not initialized. Call setDatabase() first.');
        }

        try {
            const photoRef = ref(this.database, `${this.databasePath}/${photoId}`);

            // Filter out undefined values and ensure encryption metadata is properly handled
            const cleanUpdates = Object.fromEntries(
                Object.entries(updates).filter(([_, value]) => value !== undefined)
            );

            await update(photoRef, {
                ...cleanUpdates,
                updatedAt: new Date().toISOString()
            });
        } catch (error) {
            console.error('Error updating photo post in Firebase:', error);
            throw new Error(`Failed to update photo post: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Add comment to a photo
     */
    async addCommentToPhoto(photoId: string, comment: TeamsPhotoComment): Promise<void> {
        if (!this.database) {
            throw new Error('Firebase database not initialized. Call setDatabase() first.');
        }

        try {
            const photoRef = ref(this.database, `${this.databasePath}/${photoId}`);
            const snapshot = await get(photoRef);

            if (!snapshot.exists()) {
                throw new Error('Photo not found');
            }

            const photoData = snapshot.val();
            const updatedComments = [...(photoData.comments || []), comment];

            await update(photoRef, {
                comments: updatedComments,
                updatedAt: new Date().toISOString()
            });
        } catch (error) {
            console.error('Error adding comment to photo in Firebase:', error);
            throw new Error(`Failed to add comment: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Update photo likes
     */
    async updatePhotoLikes(photoId: string, newLikeCount: number): Promise<void> {
        if (!this.database) {
            throw new Error('Firebase database not initialized. Call setDatabase() first.');
        }

        try {
            const photoRef = ref(this.database, `${this.databasePath}/${photoId}`);

            await update(photoRef, {
                likes: newLikeCount,
                updatedAt: new Date().toISOString()
            });
        } catch (error) {
            console.error('Error updating photo likes in Firebase:', error);
            throw new Error(`Failed to update likes: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Delete a photo post
     */
    async deletePhotoPost(photoId: string): Promise<void> {
        if (!this.database) {
            throw new Error('Firebase database not initialized. Call setDatabase() first.');
        }

        try {
            const photoRef = ref(this.database, `${this.databasePath}/${photoId}`);
            await remove(photoRef);
        } catch (error) {
            console.error('Error deleting photo post from Firebase:', error);
            throw new Error(`Failed to delete photo post: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Delete a photo post from Firebase only (simple deletion)
     */
    async deletePhoto(photoId: string): Promise<void> {
        if (!this.database) {
            throw new Error('Firebase database not initialized. Call setDatabase() first.');
        }

        try {
            const photoRef = ref(this.database, `${this.databasePath}/${photoId}`);
            await remove(photoRef);
            console.log(`Photo post ${photoId} deleted from Firebase successfully`);
        } catch (error) {
            console.error('Error deleting photo post from Firebase:', error);
            throw new Error(`Failed to delete photo post: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Get user's photo posts
     */
    async getUserPhotos(userId: string): Promise<TeamsPhotoPost[]> {
        if (!this.database) {
            throw new Error('Firebase database not initialized. Call setDatabase() first.');
        }

        try {
            const photosRef = ref(this.database, this.databasePath);
            const userPhotosQuery = query(
                photosRef,
                orderByChild('userId'),
                equalTo(userId)
            );

            const snapshot = await get(userPhotosQuery);
            const photos: TeamsPhotoPost[] = [];

            if (snapshot.exists()) {
                snapshot.forEach((childSnapshot: DataSnapshot) => {
                    const photoData = childSnapshot.val();
                    photos.push({
                        ...photoData,
                        id: childSnapshot.key || photoData.id
                    });
                });
            }

            // Sort by posted date (newest first)
            return photos.sort((a, b) => new Date(b.postedAt).getTime() - new Date(a.postedAt).getTime());
        } catch (error) {
            console.error('Error getting user photos from Firebase:', error);
            throw new Error(`Failed to get user photos: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Get recent photos across all challenges
     */
    async getRecentPhotos(limit: number = 20): Promise<TeamsPhotoPost[]> {
        if (!this.database) {
            throw new Error('Firebase database not initialized. Call setDatabase() first.');
        }

        try {
            const photosRef = ref(this.database, this.databasePath);
            const recentPhotosQuery = query(
                photosRef,
                orderByChild('postedAt')
            );

            const snapshot = await get(recentPhotosQuery);
            const photos: TeamsPhotoPost[] = [];

            if (snapshot.exists()) {
                snapshot.forEach((childSnapshot: DataSnapshot) => {
                    const photoData = childSnapshot.val();
                    photos.push({
                        ...photoData,
                        id: childSnapshot.key || photoData.id
                    });
                });
            }

            // Sort by posted date (newest first) and limit results
            return photos
                .sort((a, b) => new Date(b.postedAt).getTime() - new Date(a.postedAt).getTime())
                .slice(0, limit);
        } catch (error) {
            console.error('Error getting recent photos from Firebase:', error);
            throw new Error(`Failed to get recent photos: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Search photos by caption or user
     */
    async searchPhotos(searchTerm: string): Promise<TeamsPhotoPost[]> {
        if (!this.database) {
            throw new Error('Firebase database not initialized. Call setDatabase() first.');
        }

        try {
            const photosRef = ref(this.database, this.databasePath);
            const snapshot = await get(photosRef);
            const photos: TeamsPhotoPost[] = [];

            if (snapshot.exists()) {
                snapshot.forEach((childSnapshot: DataSnapshot) => {
                    const photoData = childSnapshot.val();
                    const searchableText = `${photoData.caption || ''} ${photoData.userDisplayName || ''}`.toLowerCase();

                    if (searchableText.includes(searchTerm.toLowerCase())) {
                        photos.push({
                            ...photoData,
                            id: childSnapshot.key || photoData.id
                        });
                    }
                });
            }

            // Sort by posted date (newest first)
            return photos.sort((a, b) => new Date(b.postedAt).getTime() - new Date(a.postedAt).getTime());
        } catch (error) {
            console.error('Error searching photos in Firebase:', error);
            throw new Error(`Failed to search photos: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }
}
